import { useI18N } from "@sundae/react-hooks";
import { Button, Dialog } from "@sundae/ui-toolkit";
import { useWalletDetails } from "@sundae/wallet-react";
import { ILockConfigArgs, YieldFarmingLucid } from "@sundaeswap/yield-farming";
import { useMutation } from "@tanstack/react-query";
import cx from "classnames";
import { AnimatePresence } from "framer-motion";
import { Dispatch, FC, SetStateAction, Suspense, useCallback, useEffect, useMemo } from "react";

import { testIds } from "../../../constants/testIds.constants";
import { useYieldFarmingPositions } from "../../../hooks/farms/useYieldFarmingPositions";
import { useTransactionErrorHandler } from "../../../hooks/useTransactionErrorHandler";
import SubmittingUpdatePosition from "../../../i18n/components/farms/SubmittingUpdatePosition/SubmittingUpdatePosition.trans";
import { useAppContext } from "../../../stores";
import { ETransactionBuilderState } from "../../../stores/ExchangeContext";
import { ETransactionState } from "../../../types/Transaction.types";
import { TransactionErrorHandler } from "../../../utils/transactions/TransactionErrorHandler.class";
import LoadingComponent from "../../LoadingComponent/LoadingComponent";
import Motion from "../../Motion/Motion";
import OrderSuccess from "../../OrderComponents/OrderSuccess";
import PositionList from "../../YieldFarmingComponents/PositionList/PositionList";
import { useYieldFarmingPositionDialogContext } from "../../YieldFarmingComponents/PositionTable/context";

export interface IUnlockYieldFarmingPositionsDialog {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  closeDialog: () => void;
  onMutationSuccess: (txHash: string) => void;
}

const UnlockYieldFarmingPositionsDialog: FC<IUnlockYieldFarmingPositionsDialog> = ({
  open,
  onOpenChange,
  closeDialog,
  onMutationSuccess,
}) => {
  const { t } = useI18N("farms");
  const {
    state: { sdk },
  } = useAppContext();
  const {
    details: { stakingAddress, mainAddress },
  } = useWalletDetails();
  const { resetContext, dialogState, setDialogState, setTransactionBuilderState } =
    useYieldFarmingPositionDialogContext();
  const { mergedPosition } = useYieldFarmingPositions({
    beneficiary: [stakingAddress, mainAddress],
  });
  const { handleTransactionError } = useTransactionErrorHandler(() =>
    setDialogState(ETransactionState.error)
  );

  const config: Pick<ILockConfigArgs, "ownerAddress" | "existingPositions"> = useMemo(
    () => ({
      ownerAddress: mainAddress,
      existingPositions:
        mergedPosition?.inputs?.map(({ txHash, index }) => ({
          hash: txHash,
          index,
        })) ?? [],
    }),
    [mainAddress, mergedPosition?.inputs]
  );

  const updatePositionMutation = useMutation(
    ["updateYFPositionMutation", config],
    async () => {
      if (!Boolean(config.existingPositions?.length)) {
        throw new Error("You do not have any positions to remove.");
      }

      setTransactionBuilderState(ETransactionBuilderState.BUILD);
      return (
        sdk &&
        TransactionErrorHandler.handleSDKMutation(
          () => {
            const YF = new YieldFarmingLucid(sdk.builder().lucid);
            return YF.unlock(config);
          },
          undefined,
          () => setTransactionBuilderState(ETransactionBuilderState.SUCCESS)
        )
      );
    },
    {
      cacheTime: 0,
      onError({ error, transactionState, cbor }) {
        setTransactionBuilderState(ETransactionBuilderState.ERROR);
        handleTransactionError(
          error,
          {
            resolutionButtonText: t("transaction.error_reset"),
            resolutionAction: () => {
              resetContext();
              setDialogState(ETransactionState.initial);
            },
          },
          transactionState,
          config,
          cbor
        );
      },
      onMutate() {
        setTransactionBuilderState(ETransactionBuilderState.BUILD);
      },
      onSuccess(txHash) {
        txHash && onMutationSuccess(txHash);
        setDialogState(ETransactionState.success);
        return txHash;
      },
    }
  );

  const handleUnlock = useCallback(async () => {
    setDialogState(ETransactionState.submitting);
    await updatePositionMutation.mutateAsync();
  }, [updatePositionMutation.mutateAsync, setDialogState]);

  const isSuccess = useMemo(() => dialogState === ETransactionState.success, [dialogState]);
  const isSubmitting = useMemo(() => dialogState === ETransactionState.submitting, [dialogState]);
  const isInitial = useMemo(
    () =>
      [
        ETransactionState.initial,
        ETransactionState.previewCancelled,
        ETransactionState.submittingCancelled,
        ETransactionState.reset,
      ].includes(dialogState),
    [dialogState]
  );

  const renderContent = useCallback(() => {
    if (isSuccess) {
      return (
        <OrderSuccess
          className="mb-4"
          onCancel={closeDialog}
          onSuccess={closeDialog}
          onSuccessButtonText={t("close", { ns: "common" })}
        />
      );
    }

    if (isSubmitting) {
      return (
        <Suspense fallback={<LoadingComponent size="medium" />}>
          <SubmittingUpdatePosition />
        </Suspense>
      );
    }

    if (isInitial) {
      return (
        <Motion className="grid gap-4">
          <PositionList />

          {/* -------------------------------------------------------------------------------------------------
           * Action Buttons
           * -----------------------------------------------------------------------------------------------*/}
          <div className="grid w-full gap-2 lg:flex lg:items-center">
            <Button className="w-full" onClick={handleUnlock}>
              {t("portfolio.positions.unlock")}
            </Button>
          </div>
        </Motion>
      );
    }
  }, [handleUnlock, isInitial, isSubmitting]);

  /** Reset Dialog on unmount */
  useEffect(() => {
    return () => {
      resetContext();
    };
  }, []);

  return (
    <Dialog
      description={t("portfolio.positions.unlock", { context: "describe" })}
      open={open}
      onOpenChange={onOpenChange}
      closeDialog={closeDialog}
      title={t("portfolio.positions.unlock")}
      cardContainerClassName="lg:!w-[35rem]"
    >
      <div
        className={cx("grid min-h-[18rem] gap-4 xl:gap-5", {
          "content-start": !isSuccess && !isSubmitting,
        })}
        data-testid={testIds.yieldFarming.positions.dialogs.unlock}
      >
        <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>
      </div>
    </Dialog>
  );
};

export default UnlockYieldFarmingPositionsDialog;
